import { Card, CardBody, CardHeader, Select, Skeleton, Surface } from '@chopchop-se/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { LotRow } from '@warehouse/shared';
import { ChevronLeft } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';

import { warehouseApi, warehouseKeys } from '../api';
import { ExpiryCell } from '../components/ExpiryCell';
import { MovementHistory } from '../components/MovementHistory';
import { DataTable, type DataTableColumn } from '../../../components/DataTable';
import { PageHeader } from '../../../components/PageHeader';
import { useListState } from '../../../hooks/useListState';
import { formatDateTime, formatQty } from '../../../lib/format';
import { listQueryKey } from '../../../lib/list-query';
import { useLiveQuery } from '../../../realtime/useLiveQuery';

/** Lot register — the entry point for "where did batch X end up". */
export function LotsPage(): ReactNode {
  const { t } = useTranslation(['warehouseCore', 'common']);
  const navigate = useNavigate();
  const { state, setState, setFilter } = useListState({ defaultSort: 'bestBefore', defaultDir: 'asc' });

  const { data, isFetching, error } = useQuery({
    queryKey: warehouseKeys.lots(listQueryKey(state)),
    queryFn: () => warehouseApi.listLots(state),
    placeholderData: keepPreviousData,
  });
  useLiveQuery(['lots', 'stock'], warehouseKeys.lots());

  const columns: DataTableColumn<LotRow>[] = [
    {
      id: 'lotNo',
      header: t('warehouseCore:lots.lotNo'),
      sortField: 'lotNo',
      cell: (row) => <span className="font-mono text-sm text-ink">{row.lotNo}</span>,
    },
    {
      id: 'article',
      header: t('warehouseCore:lots.article'),
      sortField: 'articleNo',
      cell: (row) => (
        <div className="min-w-0">
          <p className="truncate text-sm text-ink">{row.articleName}</p>
          <p className="font-mono text-xs text-ink-muted">{row.articleNo}</p>
        </div>
      ),
    },
    {
      id: 'bestBefore',
      header: t('warehouseCore:lots.bestBefore'),
      sortField: 'bestBefore',
      cell: (row) => <ExpiryCell date={row.bestBefore} />,
    },
    {
      id: 'supplier',
      header: t('warehouseCore:lots.supplier'),
      cell: (row) => row.supplierName ?? '—',
      hideBelow: 'lg',
    },
    {
      id: 'qty',
      header: t('warehouseCore:lots.quantity'),
      align: 'right',
      cell: (row) => <span className="font-mono">{formatQty(row.qtyBase)}</span>,
    },
    {
      id: 'bins',
      header: t('warehouseCore:lots.binCount'),
      align: 'right',
      cell: (row) => row.binCount,
      hideBelow: 'md',
    },
    {
      id: 'received',
      header: t('warehouseCore:lots.receivedAt'),
      sortField: 'receivedAt',
      cell: (row) => formatDateTime(row.receivedAt),
      hideBelow: 'xl',
    },
  ];

  return (
    <>
      <PageHeader title={t('warehouseCore:lots.title')} subtitle={t('warehouseCore:lots.subtitle')} />

      <DataTable
        columns={columns}
        rows={data?.items ?? []}
        rowKey={(row) => row.id}
        state={state}
        onStateChange={setState}
        total={data?.total ?? null}
        loading={isFetching}
        error={error}
        searchPlaceholder={t('warehouseCore:lots.search')}
        emptyTitle={t('warehouseCore:lots.empty')}
        emptyDescription={t('warehouseCore:lots.emptyHelp')}
        onRowClick={(row) => {
          void navigate(`/warehouse/lots/${row.id}`);
        }}
        filters={
          <>
            <Select
              aria-label={t('warehouseCore:lots.inStock')}
              className="w-40"
              value={state.filters?.inStock === true ? 'yes' : ''}
              options={[
                { value: '', label: t('warehouseCore:common.all') },
                { value: 'yes', label: t('warehouseCore:lots.inStock') },
              ]}
              onChange={(event) => {
                setFilter('inStock', event.target.value === 'yes' ? true : undefined);
              }}
            />
            <label className="flex items-center gap-2 text-sm text-ink-muted">
              {t('warehouseCore:lots.expiresBefore')}
              <input
                type="date"
                className="rounded-lg border border-surface-border bg-surface px-2 py-1 text-sm text-ink"
                value={String(state.filters?.bestBeforeTo ?? '')}
                onChange={(event) => {
                  setFilter('bestBeforeTo', event.target.value || undefined);
                }}
              />
            </label>
          </>
        }
      />
    </>
  );
}

/** One lot: where it currently sits and every movement it has ever made. */
export function LotDetailPage(): ReactNode {
  const { t } = useTranslation(['warehouseCore', 'common']);
  const { id = '' } = useParams<{ id: string }>();

  const { data, isPending, error } = useQuery({
    queryKey: warehouseKeys.lot(id),
    queryFn: () => warehouseApi.getLot(id),
    enabled: id.length > 0,
  });
  useLiveQuery(['stock', 'lots'], warehouseKeys.lot(id));

  if (isPending) {
    return (
      <div className="flex flex-col gap-3">
        <Skeleton className="h-8 w-64 rounded" />
        <Skeleton className="h-48 w-full rounded-2xl" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <Surface variant="subtle" className="p-6 text-sm text-ink-muted">
        {t('warehouseCore:lots.empty')}
      </Surface>
    );
  }

  return (
    <>
      <PageHeader
        breadcrumb={
          <Link to="/warehouse/lots" className="inline-flex items-center gap-1 hover:text-ink">
            <ChevronLeft className="size-3.5" aria-hidden />
            {t('warehouseCore:lots.title')}
          </Link>
        }
        title={t('warehouseCore:lots.detail', { lotNo: data.lotNo })}
        subtitle={`${data.articleNo} · ${data.articleName}`}
      />

      <div className="grid gap-5 lg:grid-cols-[20rem_minmax(0,1fr)]">
        <Card>
          <CardHeader>
            <h2 className="font-display text-base font-semibold text-ink">{data.lotNo}</h2>
          </CardHeader>
          <CardBody className="flex flex-col gap-3 text-sm">
            <Row label={t('warehouseCore:lots.bestBefore')}>
              <ExpiryCell date={data.bestBefore} />
            </Row>
            <Row label={t('warehouseCore:lots.supplier')}>{data.supplierName ?? '—'}</Row>
            <Row label={t('warehouseCore:lots.receivedAt')}>{formatDateTime(data.receivedAt)}</Row>
            <Row label={t('warehouseCore:lots.quantity')}>
              <span className="font-mono">{formatQty(data.qtyBase)}</span>
            </Row>
            <Row label={t('warehouseCore:lots.binCount')}>{data.binCount}</Row>
          </CardBody>
        </Card>

        <div className="flex flex-col gap-5">
          <Card>
            <CardHeader>
              <h2 className="font-display text-base font-semibold text-ink">
                {t('warehouseCore:lots.contents')}
              </h2>
            </CardHeader>
            <CardBody>
              {data.contents.length === 0 ? (
                <Surface variant="subtle" className="p-4 text-sm text-ink-muted">
                  {t('warehouseCore:bins.noContents')}
                </Surface>
              ) : (
                <ul className="flex flex-col gap-2">
                  {data.contents.map((line) => (
                    <li
                      key={line.stockId}
                      className="flex items-center justify-between gap-3 rounded-lg border border-surface-border px-3 py-2"
                    >
                      <span className="font-mono text-sm text-ink">{line.binCode}</span>
                      <span className="truncate text-xs text-ink-muted">{line.lpn ?? '—'}</span>
                      <span className="shrink-0 font-mono text-sm">
                        {formatQty(line.qtyBase, { unit: line.unitCode ?? undefined })}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <h2 className="font-display text-base font-semibold text-ink">
                {t('warehouseCore:lots.history')}
              </h2>
            </CardHeader>
            <CardBody>
              <MovementHistory rows={data.history} />
            </CardBody>
          </Card>
        </div>
      </div>
    </>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="font-mono text-xs uppercase tracking-[0.14em] text-ink-muted">{label}</span>
      <span className="text-ink">{children}</span>
    </div>
  );
}
